import {
	Alert,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
	useTheme
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SubmitHandler } from "react-hook-form";
import { z } from "zod";
import {
	FormInput,
	FormPhoneInput,
	httpService,
	Input,
	InputLabel,
	isEmpty,
	useForm,
	useMyAccount,
	usePopupAlert,
	zodValidator
} from "@sal/onevent-portal";

type RequestCancellationState = {
	reference: string;
	subject: string;
	reason?: string;
	name?: string;
	phone?: string;
	email?: string;
};

const requestCancellationValidation = z
	.object({
		reference: z.string(),
		subject: z.string(),
		reason: z.string({
			invalid_type_error: "Please provide a reason for cancellation.",
			required_error: "Please provide a reason for cancellation."
		}),
		name: z.string({
			invalid_type_error: "Please provide your name.",
			required_error: "Please provide your name."
		}),
		phone: zodValidator.phone.or(zodValidator.empty),
		email: zodValidator.email.nullable()
	})
	.superRefine((data: { phone?: string | null; email?: string | null }, ctx) => {
		if (isEmpty(data.phone) && isEmpty(data.email)) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please provide a phone number.",
				path: ["phone"]
			});
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please provide an email address.",
				path: ["email"]
			});
		}
	});

type RequestCancellationModalProps = {
	reference: string;
	defaultValues: Partial<RequestCancellationState>;
	alreadySubmitted: boolean;
	onClose: () => void;
	onSubmit: (data: RequestCancellationState) => Promise<void>;
};

function RequestCancellationModal({
	reference,
	defaultValues,
	alreadySubmitted,
	onClose,
	onSubmit
}: RequestCancellationModalProps) {
	const theme = useTheme();
	const subject = `Cancellation Request - ${reference}`;
	const [isSubmitting, setIsSubmitting] = useState(false);

	const onSubmitHandler: SubmitHandler<RequestCancellationState> = async (state) => {
		if (isSubmitting) {
			return;
		}

		setIsSubmitting(true);
		try {
			await onSubmit(state);
			onClose();
		} catch {
			// Submission failed; keep the modal open so the user can retry. The error is surfaced as a popup by onSubmit.
		} finally {
			setIsSubmitting(false);
		}
	};

	const [context, Form] = useForm<RequestCancellationState>({
		defaultValues: { ...defaultValues, reference, subject },
		onSubmit: onSubmitHandler,
		validationSchema: requestCancellationValidation
	});
	const { clearErrors, reset } = context;

	useEffect(() => {
		reset({ ...defaultValues, reference, subject });
	}, [defaultValues.name, defaultValues.phone, defaultValues.email, reference, subject]);

	return (
		<Dialog
			fullWidth
			open
			onClose={(_, reason) => {
				if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
					onClose();
				}
			}}
			scroll="paper"
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					color: theme.palette.common.black
				}}
			>
				<Typography sx={{ fontWeight: 300, fontSize: "1.5rem" }}>Request Cancellation</Typography>
				<IconButton onClick={onClose} sx={{ padding: 0 }} edge="end" color="inherit" aria-label="close">
					<Close sx={{ flexGrow: 1 }} />
				</IconButton>
			</DialogTitle>
			<Form>
				<DialogContent>
					<Grid container spacing={2}>
						{alreadySubmitted && (
							<Grid item xs={12} sx={{ padding: theme.spacing(2) }}>
								<Alert severity="info">
									A cancellation request has already been submitted for this consignment in the
									current session. Another request cannot be sent from this page.
								</Alert>
							</Grid>
						)}
						<Grid item xs={12} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="reference" label="Consignment Number" />
							<Input name="reference" value={reference} readOnly />
						</Grid>
						<Grid item xs={12} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="subject" label="Subject" />
							<Input name="subject" value={subject} readOnly />
						</Grid>
						<Grid item xs={12} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="reason" label="Reason for Cancellation" required />
							<FormInput name="reason" multiline minRows={4} />
						</Grid>
						<Grid item xs={12} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="name" label="Sender Name" required />
							<FormInput name="name" />
						</Grid>
						<Grid item xs={6} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="phone" label="Phone" />
							<FormPhoneInput name="phone" onChange={() => clearErrors("email")} maxDigits={15} />
						</Grid>
						<Grid item xs={6} sx={{ padding: theme.spacing(2) }}>
							<InputLabel name="email" label="Email" />
							<FormInput name="email" onChange={() => clearErrors("phone")} />
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions sx={{ padding: 4 }}>
					<Button
						type="submit"
						variant="contained"
						color="primary"
						disabled={alreadySubmitted || isSubmitting}
						startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
					>
						{isSubmitting ? "Sending..." : "Send"}
					</Button>
				</DialogActions>
			</Form>
		</Dialog>
	);
}

type RequestCancellationProps = {
	id: number;
	reference: string;
	alreadySubmitted: boolean;
	onClose: () => void;
	onSubmitted: () => void;
};

export function RequestCancellation({
	id,
	reference,
	alreadySubmitted,
	onClose,
	onSubmitted
}: RequestCancellationProps) {
	const popup = usePopupAlert();
	const { userDetail } = useMyAccount();

	const defaultValues: Partial<RequestCancellationState> = {
		name: userDetail?.fullName,
		phone: userDetail?.phoneNumber,
		email: userDetail?.email
	};

	const handleSend = async (data: RequestCancellationState) => {
		const payload = {
			id,
			reference: data.reference,
			name: data.name,
			phone: !isEmpty(data.phone) ? data.phone : null,
			email: data.email,
			reason: data.reason,
			toAddress: ""
		};

		try {
			const response = await httpService.post(
				`ContainerTransportGeneralFreightOrder/RequestCancellation/${id}`,
				payload
			);
			if (response) {
				onSubmitted();
				popup({
					message: "Cancellation request has been sent",
					severity: "success"
				});
			}
		} catch (error) {
			popup({
				message: error instanceof Error ? error.message : "Error sending cancellation request",
				severity: "error"
			});
		}
	};

	return createPortal(
		<RequestCancellationModal
			reference={reference}
			defaultValues={defaultValues}
			alreadySubmitted={alreadySubmitted}
			onClose={onClose}
			onSubmit={handleSend}
		/>,
		document.body
	);
}
